import { Cookies } from '@kulee/helper'
import { getBaseHost, RESPONSE_CODES, STORAGE_KEYS } from '@kulee/tga-constant'
import axios from 'axios'
import logger from '../logger'
import { generateRequestSign, handleRequestError, RequestError } from './tool'

import type { AxiosInstance } from 'axios'

function getRequestInstance(options: GetRequestInstanceOptions): AxiosInstance {
  const { env, appId, lang, requestConfig = {} } = options
  const { error = () => {} } = requestConfig

  const userTokenStorageKey = STORAGE_KEYS.USER_TOKEN + '-' + appId

  // 是否正在获取token
  let gettingToken = false
  let waitingCalls: Array<any> = []

  const axiosInstance = axios.create({
    timeout: 15000
  })

  axiosInstance.interceptors.request.use(
    config => {
      config.headers = config.headers || {}
      const token = JSON.parse(Cookies.get(userTokenStorageKey) || '{}')
      // 时区名称
      const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone

      token.accessToken && (config.headers.Authorization = token.accessToken)
      appId && (config.headers['appId'] = appId)
      lang && (config.headers['Accept-Language'] = lang)
      config.headers['x-timezone'] = timezoneName

      if (config.method && config.method.toLocaleLowerCase() === 'post') {
        // 获取 POST 接口准备发送的参数
        const postData = config.data
        if (postData) {
          const sign = generateRequestSign(postData)
          sign && (config.headers['sign'] = sign)
        }

        config.data = config.data || {}

        if (Object.prototype.toString.call(config.data) === '[object String]') {
          config.data = JSON.parse(config.data)
        }
      }

      // 把confg.data是为underfined的删除
      for (const key in config.data) {
        if (config.data[key] === undefined) {
          delete config.data[key]
        }
      }

      return config
    },
    err => {
      logger.warn('[request] request error')
      err.code = RESPONSE_CODES.UNKNOWN_ERROR
      err.message = 'request error'

      error(err)
      return Promise.reject(err)
    }
  )

  axiosInstance.interceptors.response.use(
    response => {
      const {
        data: { code }
      } = response

      // 非成功，全部reject
      if (code !== 200) {
        error(response.data)
        return Promise.reject(handleRequestError(response.data))
      }

      return response.data
    },
    err => {
      logger.warn('[request] request error', err)
      const { data, config } = err.response
      const { code } = data

      const token = JSON.parse(Cookies.get(userTokenStorageKey) || '{}')
      if (code === 401 && token.refreshToken) {
        logger.warn('error auto token invalid, ready to refresh token.')

        return new Promise((resolve, reject) => {
          waitTokenRefreshAndCall({
            resolve,
            reject,
            config,
            data: err.response
          })
        })
      }

      error(err)

      return Promise.reject(handleRequestError(data))
    }
  )

  async function waitTokenRefreshAndCall(options: { resolve: any; reject: any; config: any; data: any }) {
    waitingCalls.push(options)

    if (!gettingToken) {
      gettingToken = true
      try {
        await refreshToken()
        waitingCalls.forEach(({ resolve, config }) => {
          resolve(axiosInstance.request(config))
        })

        gettingToken = false
        waitingCalls = []
      } catch (error) {
        waitingCalls.forEach(({ reject, data }) => {
          reject(handleRequestError(data))
        })
      }
    }
  }

  async function refreshToken() {
    try {
      try {
        // 在kuleWebview中
        const { token, refreshToken } = await window.Bridge.system.resetToken()
        Cookies.set(userTokenStorageKey, JSON.stringify({ accessToken: token, refreshToken }))
      } catch (error) {
        const token = JSON.parse(Cookies.get(userTokenStorageKey) || '{}')

        const {
          data: { code, data }
        } = await axios.post(`${getBaseHost(env)}/api/v1/auth/token/refresh`, {
          refreshToken: token.refreshToken
        })
        if (code === 200) {
          Cookies.set(userTokenStorageKey, JSON.stringify(data))

          try {
            window.Bridge.system.resetToken({
              token: data.accessToken,
              refreshToken: data.refreshToken
            })
          } catch (error) {}
        } else {
          throw new Error('')
        }
      }
    } catch (error) {
      logger.warn('[request] refresh token error', error)
      Cookies.remove(userTokenStorageKey)

      throw error
    }
  }

  return axiosInstance
}

interface GetRequestInstanceOptions {
  env: EnvType
  appId?: string
  lang?: string
  requestConfig?: RequestConfig
}

interface RequestConfig {
  error?: (error: Error & RequestError) => void
}

export default getRequestInstance
